import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, MapPin, Plane, AlertCircle, Clock, Check } from "lucide-react";
import { getAirlineColors, getAirlineCode, getFlightStatus } from "@/utils/airlineUtils";
import { fmtChicagoWeekday } from "@/utils/timezone";

export function DailyPassengersChart({ stats }) {
  if (!stats?.daily_passengers) return null;

  return (
    <Card className="border-0 shadow-md mb-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <BarChart3 className="w-5 h-5" />
          Daily Passengers & Destinations (Scroll for more days)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
          <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
            {Object.entries(stats.daily_passengers).map(([date, data], index) => {
              const maxPassengers = Math.max(...Object.values(stats.daily_passengers).map(d => d.total), 1);
              const heightPercent = (data.total / maxPassengers) * 100;
              const isToday = date === new Date().toISOString().split('T')[0];
              const weekNum = Math.floor(index / 7) + 1;
              const dayDestinations = stats.daily_destinations?.[date] || {};
              const hasDestinations = Object.keys(dayDestinations).length > 0;
              
              return (
                <div key={date} className="flex flex-col items-center group relative" style={{ minWidth: '100px' }}>
                  <div className="w-full bg-gray-100 rounded-lg h-20 flex items-end justify-center p-1 relative cursor-pointer">
                    <div 
                      className={`w-full rounded-md transition-all ${isToday ? 'bg-green-500' : weekNum === 1 ? 'bg-blue-500' : weekNum === 2 ? 'bg-blue-400' : weekNum === 3 ? 'bg-blue-300' : 'bg-blue-200'}`}
                      style={{ height: `${Math.max(heightPercent, 8)}%` }}
                    />
                  </div>
                  <p className={`text-lg font-bold mt-1 ${isToday ? 'text-green-600' : ''}`}>{data.total}</p>
                  <p className="text-xs text-muted-foreground text-center font-medium">
                    {isToday ? 'Today' : fmtChicagoWeekday(date + 'T12:00:00Z')}
                  </p>
                  <p className="text-xs text-muted-foreground">{date.slice(5)}</p>
                  
                  {hasDestinations && (
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-white border shadow-xl rounded-lg p-3 min-w-[180px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      <p className="font-bold text-sm mb-2 border-b pb-1">{date}</p>
                      <div className="space-y-1">
                        {Object.entries(dayDestinations)
                          .sort((a, b) => b[1] - a[1])
                          .map(([dest, count]) => (
                            <div key={dest} className="flex justify-between text-xs">
                              <span className="truncate mr-2" style={{ maxWidth: '120px' }} title={dest}>{dest}</span>
                              <span className="font-bold text-green-600">{count}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span> Today</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded"></span> Week 1</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-400 rounded"></span> Week 2</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-300 rounded"></span> Week 3</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-200 rounded"></span> Week 4</span>
          <span className="ml-auto text-muted-foreground">Hover over days to see destination breakdown</span>
        </div>
      </CardContent>
    </Card>
  );
}

export function DestinationCountsChart({ stats }) {
  if (!stats?.destination_counts || Object.keys(stats.destination_counts).length === 0) return null;

  return (
    <Card className="border-0 shadow-md mb-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <MapPin className="w-5 h-5" />
          Total Bookings by Drop-off Location (All Time)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(stats.destination_counts)
            .sort((a, b) => b[1] - a[1])
            .map(([destination, count]) => (
              <div key={destination} className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
                <span className="text-sm font-medium truncate" title={destination}>{destination}</span>
                <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">{count}</span>
              </div>
            ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function FlightGroupingsChart({ stats }) {
  if (!stats?.flight_counts || Object.keys(stats.flight_counts).length === 0) return null;

  return (
    <Card className="border-0 shadow-md mb-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Plane className="w-5 h-5" />
          Passengers by Flight (All Bookings)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Mobile compact view: one card per flight, no horizontal scroll */}
        <div className="md:hidden space-y-2">
          {Object.entries(stats.flight_counts)
            .sort((a, b) => b[1].total_passengers - a[1].total_passengers)
            .map(([flightNum, data]) => {
              const airlineCode = getAirlineCode(flightNum);
              const colors = getAirlineColors(airlineCode);
              const status = getFlightStatus(data.status, data.delayed);
              return (
                <div key={flightNum} className="flex items-center gap-2 p-2 rounded-lg border hover:bg-gray-50">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-white font-bold text-xs flex-shrink-0 ${colors.bg}`}>
                    {airlineCode || '??'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-sm truncate ${colors.text}`}>{flightNum}</p>
                    <p className="text-[10px] text-gray-500 truncate">
                      {data.airline}{data.terminal ? ` · T${data.terminal}` : ''}
                    </p>
                  </div>
                  <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${status.bg} ${status.color}`}>
                    {status.isDelayed ? <AlertCircle className="w-2.5 h-2.5" /> :
                      (status.label === "On Time" || status.label === "Landed") ? <Check className="w-2.5 h-2.5" /> :
                      <Clock className="w-2.5 h-2.5" />}
                    <span className="hidden xs:inline">{status.label}</span>
                  </div>
                  <span className={`px-2 py-0.5 ${colors.light} ${colors.text} rounded-full font-bold text-xs flex-shrink-0`}>
                    {data.total_passengers}
                  </span>
                </div>
              );
            })}
        </div>

        {/* Desktop / tablet: full table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Flight #</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Airline</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Terminal</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-right p-3 text-sm font-medium text-muted-foreground">Passengers</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.flight_counts)
                .sort((a, b) => b[1].total_passengers - a[1].total_passengers)
                .map(([flightNum, data]) => {
                  const airlineCode = getAirlineCode(flightNum);
                  const colors = getAirlineColors(airlineCode);
                  const status = getFlightStatus(data.status, data.delayed);
                  
                  return (
                    <tr key={flightNum} className={`border-b hover:${colors.light}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-white font-bold text-xs ${colors.bg}`}>
                            {airlineCode || '??'}
                          </span>
                          <span className={`font-bold ${colors.text}`}>{flightNum}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="font-medium">{data.airline}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-gray-600">{data.terminal ? `Terminal ${data.terminal}` : 'TBD'}</span>
                      </td>
                      <td className="p-3">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.color}`}>
                          {status.isDelayed ? (
                            <AlertCircle className="w-3 h-3" />
                          ) : status.label === "On Time" || status.label === "Landed" ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <Clock className="w-3 h-3" />
                          )}
                          {status.label}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <span className={`px-3 py-1 ${colors.light} ${colors.text} rounded-full font-bold`}>
                          {data.total_passengers}
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
